// src/store/schoolStore.js
//
// District tenancy (Phase 3): the app's notion of "which school am I working in".
// See Developer/DISTRICT_TENANCY_DESIGN.md §9.
//
// Single-school projects are unaffected: there is exactly one available school,
// `isMultiSchool` is false, the switcher stays hidden, and currentSchoolId is the
// one school. The store only becomes visible/active once a district holds 2+
// schools (or the user is a district-level role).

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { auditLogger } from '@/utils/auditLogger'

const STORAGE_KEY = 'currentSchoolId'
const DISTRICT_LEVEL_ROLES = ['district_admin', 'district_coordinator']

/**
 * Pure derivation of which schools a user may switch between.
 * Extracted for unit testing (no Firestore / Pinia needed).
 *
 * @param {Object} args
 * @param {Array<{id:string,name?:string,active?:boolean}>} args.allSchools  schools registry
 * @param {string[]|null|undefined} args.userSchoolIds  the user's schoolIds
 * @param {boolean} args.isDistrictLevel  district_admin / district_coordinator
 * @param {boolean} args.canAccessAllSchools  district case manager (allSchools flag)
 * @returns {Array<{id:string,name:string}>} sorted by name
 */
export function computeAvailableSchools({
  allSchools = [],
  userSchoolIds,
  isDistrictLevel = false,
  canAccessAllSchools = false
}) {
  const ids = Array.isArray(userSchoolIds) ? userSchoolIds : []
  const visible = allSchools
    // district-level roles + allSchools case managers reach every school;
    // everyone else only their own
    .filter((s) => isDistrictLevel || canAccessAllSchools || ids.includes(s.id))
    // hide explicitly-deactivated schools
    .filter((s) => s.active !== false)
    .map((s) => ({ id: s.id, name: s.name || s.id }))

  visible.sort((a, b) => a.name.localeCompare(b.name))
  return visible
}

/**
 * Narrow a materialized student list to one school.
 *
 * Applied whenever `currentSchoolId` is set (including single-school staff in a
 * multi-school district). LENIENT for un-stamped docs — kept until backfill
 * completes. Server-side list queries should also filter by schoolId (Phase 3b).
 */
export function scopeStudentsToSchool(students = [], schoolId = null) {
  if (!schoolId) return students
  return students.filter((s) => !s || !s.schoolId || s.schoolId === schoolId)
}

/**
 * Narrow a user list to members of one school.
 *
 * LENIENT for users with no school assignment (legacy docs, district-wide
 * roles): they stay visible in every school's admin panel rather than
 * disappearing from all of them.
 */
export function scopeUsersToSchool(users = [], schoolId = null) {
  if (!schoolId) return users
  return users.filter((u) => {
    if (!u) return true
    const ids = Array.isArray(u.schoolIds) ? u.schoolIds : []
    if (ids.length === 0 && !u.schoolId) return true
    return ids.includes(schoolId) || u.schoolId === schoolId
  })
}

/**
 * Whether this user's home campus is `schoolId`.
 *
 * `schoolIds` can list every campus they can open (itinerant add-campus,
 * leftover demo accounts). Dashboard role tiles should count people *based*
 * here, not everyone who can switch into the school.
 */
export function userHomedAtSchool(user, schoolId) {
  if (!schoolId || !user) return false
  const primary = user.primarySchoolId || user.schoolId || null
  if (primary) return primary === schoolId
  const ids = Array.isArray(user.schoolIds) ? user.schoolIds.filter(Boolean) : []
  return ids.length === 1 && ids[0] === schoolId
}

/** Narrow a user list to staff whose home campus is `schoolId`. */
export function usersHomedAtSchool(users = [], schoolId = null) {
  if (!schoolId) return users
  return users.filter((u) => userHomedAtSchool(u, schoolId))
}

/**
 * Choose the active school id given what is available and any prior preference.
 * @param {string[]} availableIds
 * @param {Object} opts
 * @param {string|null} opts.stored      previously selected id (localStorage)
 * @param {string|null} opts.primary     user's primarySchoolId
 * @returns {string|null}
 */
export function resolveCurrentSchoolId(
  availableIds,
  { stored = null, primary = null, fromUrl = null } = {}
) {
  if (fromUrl && availableIds.includes(fromUrl)) return fromUrl
  if (stored && availableIds.includes(stored)) return stored
  if (primary && availableIds.includes(primary)) return primary
  return availableIds[0] || null
}

export const useSchoolStore = defineStore('school', () => {
  const allSchools = ref([]) // raw schools registry docs
  const currentSchoolId = ref(null)
  const loading = ref(false)
  const loaded = ref(false)

  const authStore = useAuthStore()

  const isDistrictLevel = computed(() => DISTRICT_LEVEL_ROLES.includes(authStore.currentUser?.role))

  // District case manager: a school-scoped role flagged allSchools reaches every
  // school (switcher lists all), but without district-dashboard access.
  const canAccessAllSchools = computed(
    () => isDistrictLevel.value || authStore.currentUser?.allSchools === true
  )

  const availableSchools = computed(() =>
    computeAvailableSchools({
      allSchools: allSchools.value,
      userSchoolIds: authStore.currentUser?.schoolIds,
      isDistrictLevel: isDistrictLevel.value,
      canAccessAllSchools: canAccessAllSchools.value
    })
  )

  // The switcher only appears when there is a genuine choice to make.
  const isMultiSchool = computed(() => availableSchools.value.length > 1)

  /** District holds 2+ active schools in the registry (not just user assignment). */
  const isDistrictMultiSchool = computed(
    () => allSchools.value.filter((s) => s.active !== false).length >= 2
  )

  const currentSchool = computed(
    () => availableSchools.value.find((s) => s.id === currentSchoolId.value) || null
  )

  function readStored() {
    try {
      return localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  }

  function persist(id) {
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id)
    } catch {
      /* ignore quota/availability errors */
    }
  }

  function clearStoredSchool() {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* ignore */
    }
  }

  /**
   * Ensure currentSchoolId is one of the signed-in user's available schools.
   * Re-runs when the user changes or when ?school= arrives on a cached load.
   */
  function reconcileCurrentSchool(fromUrl = null) {
    const ids = availableSchools.value.map((s) => s.id)
    const urlSchool = fromUrl && ids.includes(fromUrl) ? fromUrl : null

    if (urlSchool) {
      if (currentSchoolId.value !== urlSchool) {
        currentSchoolId.value = urlSchool
        persist(urlSchool)
      }
      return
    }

    if (currentSchoolId.value && ids.includes(currentSchoolId.value)) return

    currentSchoolId.value = resolveCurrentSchoolId(ids, {
      stored: readStored(),
      primary: authStore.currentUser?.primarySchoolId || null
    })
    if (currentSchoolId.value) persist(currentSchoolId.value)
  }

  /**
   * Load the schools registry and resolve the active school. Safe to call
   * repeatedly; only fetches once unless forced.
   *
   * Permission-denied is NOT treated as a successful load. After Google
   * sign-in the JWT sometimes lacks `role` for a beat; marking loaded on
   * that denial left district-admin with an empty registry until refresh.
   */
  let inFlight = null

  function isPermissionDenied(err) {
    const code = err?.code || ''
    const msg = String(err?.message || '')
    return code === 'permission-denied' || /insufficient permissions/i.test(msg)
  }

  async function loadSchools({ force = false, fromUrl = null } = {}) {
    if (loaded.value && !force) {
      reconcileCurrentSchool(fromUrl)
      return
    }
    if (inFlight) {
      await inFlight
      if (loaded.value && !force) {
        reconcileCurrentSchool(fromUrl)
        return
      }
    }

    loading.value = true
    const run = (async () => {
      try {
        const snap = await getDocs(collection(db, 'schools'))
        allSchools.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        loaded.value = true
      } catch (e) {
        // Pre-migration projects may have no readable schools registry yet.
        console.warn('schoolStore: could not load schools registry:', e?.message || e)
        allSchools.value = []
        loaded.value = !isPermissionDenied(e)
      } finally {
        loading.value = false
      }
    })()
    inFlight = run
    try {
      await run
    } finally {
      if (inFlight === run) inFlight = null
    }

    reconcileCurrentSchool(fromUrl)
  }

  /**
   * Apply ?school= from a deep link without the school-switch audit entry.
   * Ignores ids outside the user's available schools.
   */
  function applySchoolFromQuery(schoolId) {
    reconcileCurrentSchool(schoolId)
  }

  /**
   * Switch the active school (multi-site users / district admins). Persists the
   * choice and writes an audit entry, mirroring the design's "each entry audited".
   */
  async function setCurrentSchool(id) {
    if (!id || id === currentSchoolId.value) return
    if (!availableSchools.value.some((s) => s.id === id)) {
      console.warn(`schoolStore: refusing to switch to out-of-scope school "${id}"`)
      return
    }
    const previous = currentSchoolId.value
    currentSchoolId.value = id
    persist(id)
    try {
      await auditLogger.logSystemAccess('school_switch', { from: previous, to: id })
    } catch {
      /* audit failure must not block the switch */
    }
  }

  function reset() {
    allSchools.value = []
    currentSchoolId.value = null
    loaded.value = false
    clearStoredSchool()
  }

  // New sign-in or account switch: drop cached registry and stale school preference.
  // Wait until auth isLoading is false so the JWT already has role claims.
  watch(
    () => [authStore.currentUser?.uid, authStore.isLoading],
    ([uid, authLoading], [prevUid] = []) => {
      if (!uid) {
        reset()
        return
      }
      if (authLoading) return
      if (prevUid && uid !== prevUid) {
        allSchools.value = []
        currentSchoolId.value = null
        loaded.value = false
        clearStoredSchool()
        void loadSchools({ force: true })
        return
      }
      if (!loaded.value) void loadSchools()
    }
  )

  // schoolIds may arrive after the first auth snapshot (permissionStore sync).
  watch(
    () => authStore.currentUser?.schoolIds,
    () => {
      if (loaded.value) reconcileCurrentSchool()
    },
    { deep: true }
  )

  return {
    allSchools,
    currentSchoolId,
    currentSchool,
    availableSchools,
    isMultiSchool,
    isDistrictMultiSchool,
    isDistrictLevel,
    canAccessAllSchools,
    loading,
    loaded,
    loadSchools,
    setCurrentSchool,
    applySchoolFromQuery,
    reset
  }
})
